import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Toolbar,
  Checkbox,
} from '@mui/material';
import {
  Edit as EditIcon,
  Print as PrintIcon,
  Print as BulkPrintIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';
import NotaService from '../../services/nota.service';
import GradeService from '../../services/grade.service';
import CycleService from '../../services/cycle.service';

const NotasList = () => {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [grades, setGrades] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [selectedCycle, setSelectedCycle] = useState('');
  const [selectedGrade, setSelectedGrade] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedCycle) {
      loadStudents();
    }
  }, [selectedCycle, selectedGrade, searchTerm]);

  const loadInitialData = async () => {
    try {
      const [gradesRes, cyclesRes] = await Promise.all([
        GradeService.getAll(),
        CycleService.getAll(),
      ]);
      const gradesData = gradesRes.data?.data || [];
      const cyclesData = cyclesRes.data?.data || [];
      setGrades(gradesData);
      setCycles(cyclesData);
      // Auto-select first cycle when none selected
      if (cyclesData.length > 0) {
        setSelectedCycle(cyclesData[0].id);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      showSnackbar(t('notas.errorLoadingData'), 'error');
    }
  };

  const loadStudents = async () => {
    if (!selectedCycle) return;

    setLoading(true);
    try {
      const params = {
        cycle_id: selectedCycle,
      };

      if (selectedGrade) {
        params.grade_id = selectedGrade;
      }

      if (searchTerm) {
        params.search = searchTerm;
      }

      const response = await NotaService.getStudentsList(params);
      setStudents(response.data?.data || []);
    } catch (error) {
      console.error('Error loading students:', error);
      showSnackbar(t('notas.errorLoadingStudents'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputNotas = (asignmentId) => {
    // Navigate to input page with asignment ID and set tab to 0 (Input)
    setSearchParams({ asignment_id: asignmentId, tab: '0' });
  };

  const handlePrint = async (asignmentId) => {
    try {
      const response = await NotaService.generatePdf(asignmentId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `nota_${asignmentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showSnackbar(t('notas.pdfGeneratedSuccess'));
    } catch (error) {
      console.error('Error generating PDF:', error);
      showSnackbar(t('notas.errorGeneratingPdf'), 'error');
    }
  };

  const handleBulkPrint = async () => {
    if (!selectedCycle) {
      showSnackbar(t('notas.pleaseSelectCycle'), 'error');
      return;
    }

    try {
      const params = {
        cycle_id: selectedCycle,
      };

      if (selectedGrade) {
        params.grade_id = selectedGrade;
      }

      if (selectedStudents.length > 0) {
        params.asignment_ids = selectedStudents;
      }

      const response = await NotaService.generateBulkPdf(params);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `notas_bulk_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showSnackbar(t('notas.bulkPdfGeneratedSuccess'));
    } catch (error) {
      console.error('Error generating bulk PDF:', error);
      showSnackbar(t('notas.errorGeneratingBulkPdf'), 'error');
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedStudents(students.map(s => s.id));
    } else {
      setSelectedStudents([]);
    }
  };

  const handleSelectStudent = (asignmentId) => {
    setSelectedStudents(prev => {
      if (prev.includes(asignmentId)) {
        return prev.filter(id => id !== asignmentId);
      } else {
        return [...prev, asignmentId];
      }
    });
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const filteredStudents = students;

  return (
    <Box>
      <Card>
        <CardHeader
          title={t('notas.listCardTitle')}
          action={
            <Box display="flex" gap={2}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<BulkPrintIcon />}
                onClick={handleBulkPrint}
                disabled={!selectedCycle || loading}
              >
                {t('notas.bulkPrint')}
              </Button>
            </Box>
          }
        />
        <CardContent>
          <Box display="flex" gap={2} mb={3} flexWrap="wrap">
            <FormControl sx={{ minWidth: 200 }} required>
              <InputLabel>{t('students.cycle')} *</InputLabel>
              <Select
                value={selectedCycle}
                onChange={(e) => setSelectedCycle(e.target.value)}
                label={`${t('students.cycle')} *`}
              >
                {cycles.map((cycle) => (
                  <MenuItem key={cycle.id} value={cycle.id}>
                    {cycle.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>{t('students.grade')}</InputLabel>
              <Select
                value={selectedGrade}
                onChange={(e) => setSelectedGrade(e.target.value)}
                label={t('students.grade')}
              >
                <MenuItem value="">{t('pensums.allGrades')}</MenuItem>
                {grades.map((grade) => (
                  <MenuItem key={grade.id} value={grade.id}>
                    {grade.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label={t('notas.searchStudent')}
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 200 }}
            />
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedStudents.length > 0 && selectedStudents.length < filteredStudents.length}
                        checked={filteredStudents.length > 0 && selectedStudents.length === filteredStudents.length}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell>{t('common.name')}</TableCell>
                    <TableCell>{t('students.cycle')}</TableCell>
                    <TableCell>{t('students.grade')}</TableCell>
                    <TableCell align="center">{t('common.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredStudents.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body2" color="text.secondary">
                          {selectedCycle ? t('notas.noStudentsFound') : t('notas.pleaseSelectCycle')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredStudents.map((asignment) => (
                      <TableRow key={asignment.id} hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedStudents.includes(asignment.id)}
                            onChange={() => handleSelectStudent(asignment.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight="medium">
                            {asignment.student?.name} ({asignment.student?.code})
                          </Typography>
                        </TableCell>
                        <TableCell>{asignment.cycle?.name}</TableCell>
                        <TableCell>{asignment.grade?.name}</TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleInputNotas(asignment.id)}
                            title={t('notas.inputNotas')}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={() => handlePrint(asignment.id)}
                            title={t('notas.printPdf')}
                          >
                            <PrintIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NotasList;
